// src/pages/Admin/Business/Finance/components/CommitmentsTable.jsx

import React from "react";
import { FaFileInvoice } from "react-icons/fa";
import ButtonEdit from "../../../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../../../components/Button/ButtonDelete";
import {
  fmtMoney,
  formatDate,
  getCommitmentMeta,
} from "../financeCommitments.utils";

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    red: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    purple: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

function renderDateCell(item) {
  if (item.entityType === "depense") {
    return formatDate(item.dueDate);
  }

  const start = formatDate(item.startDate);
  const end = formatDate(item.endDate);

  if (start === end) return end;
  return (
    <div className="flex flex-col gap-1">
      <span>Début : {start}</span>
      <span>Fin : {end}</span>
    </div>
  );
}

const CommitmentsTable = ({
  items,
  onEditExpense,
  onEditDeadline,
  onDelete,
}) => {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-[1280px] w-full">
          <thead className="bg-slate-50 dark:bg-slate-800/60">
            <tr>
              <Th>Type</Th>
              <Th>Libellé</Th>
              <Th>Association</Th>
              <Th>Catégorie</Th>
              <Th>Tiers</Th>
              <Th>Date / période</Th>
              <Th>Montant</Th>
              <Th>Alerte</Th>
              <Th>Origine</Th>
              <Th>Facture</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody>
            {items.map((item) => {
              const meta = getCommitmentMeta(item);

              return (
                <tr
                  key={item.uid}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition"
                >
                  <Td className="min-w-[110px]">
                    <Badge tone={item.entityType === "depense" ? "blue" : "purple"}>
                      {item.entityType === "depense" ? "Dépense" : "Échéance"}
                    </Badge>
                  </Td>

                  <Td className="min-w-[240px]">
                    <div className="font-semibold text-slate-900 dark:text-slate-50">
                      {item.title}
                    </div>
                    {item.entityType === "echeance" && item.autoRenew ? (
                      <div className="mt-1">
                        <Badge tone="green">Auto</Badge>
                      </div>
                    ) : null}
                  </Td>

                  <Td className="min-w-[200px]">
                    <div className="font-medium text-slate-900 dark:text-slate-50">
                      {item.associationName}
                    </div>
                  </Td>

                  <Td className="min-w-[180px]">{item.category || "—"}</Td>

                  <Td className="min-w-[170px]">{item.counterparty || "—"}</Td>

                  <Td className="min-w-[170px]">{renderDateCell(item)}</Td>

                  <Td className="min-w-[160px]">
                    <div className="font-semibold text-slate-900 dark:text-slate-50">
                      {fmtMoney(item.displayAmount)}
                    </div>
                    {item.entityType === "echeance" ? (
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {item.matchedExpenseDate
                          ? `estimé via dépense du ${formatDate(item.matchedExpenseDate)}`
                          : "pas d’historique"}
                      </div>
                    ) : null}
                  </Td>

                  <Td className="min-w-[140px]">
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                  </Td>

                  <Td className="min-w-[190px]">
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      {item.originLabel}
                    </div>
                  </Td>

                  <Td className="min-w-[110px]">
                    {item.invoiceLink ? (
                      <a
                        href={item.invoiceLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        <FaFileInvoice />
                        Voir
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400">Aucune</span>
                    )}
                  </Td>

                  <Td className="min-w-[110px]">
                    <div className="flex justify-end gap-2">
                      <ButtonEdit
                        onClick={() =>
                          item.entityType === "depense"
                            ? onEditExpense(item.raw)
                            : onEditDeadline(item.raw)
                        }
                      />
                      <ButtonDelete onClick={() => onDelete(item)} />
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 p-4 md:hidden">
        {items.map((item) => {
          const meta = getCommitmentMeta(item);

          return (
            <div
              key={`mobile-${item.uid}`}
              className="rounded-[24px] border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 dark:text-slate-50">
                    {item.title}
                  </div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {item.associationName}
                  </div>
                </div>

                <Badge tone={item.entityType === "depense" ? "blue" : "purple"}>
                  {item.entityType === "depense" ? "Dépense" : "Échéance"}
                </Badge>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <MobileInfo label="Catégorie" value={item.category || "—"} />
                <MobileInfo label="Date" value={formatDate(item.dueDate || item.endDate)} />
                <MobileInfo label="Montant" value={fmtMoney(item.displayAmount, true)} />
                <MobileInfo label="Alerte" value={<Badge tone={meta.tone}>{meta.label}</Badge>} />
              </div>

              <div className="mt-4 rounded-2xl bg-white dark:bg-slate-900 p-3 text-sm text-slate-600 dark:text-slate-300">
                <div>
                  <span className="font-medium">Tiers :</span> {item.counterparty || "—"}
                </div>
                <div className="mt-2">
                  <span className="font-medium">Origine :</span> {item.originLabel}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                {item.invoiceLink ? (
                  <a
                    href={item.invoiceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400"
                  >
                    <FaFileInvoice />
                    Facture
                  </a>
                ) : (
                  <span className="text-sm text-slate-400">Pas de facture</span>
                )}

                <div className="flex gap-2">
                  <ButtonEdit
                    onClick={() =>
                      item.entityType === "depense"
                        ? onEditExpense(item.raw)
                        : onEditDeadline(item.raw)
                    }
                  />
                  <ButtonDelete onClick={() => onDelete(item)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

export default CommitmentsTable;